import React, { useEffect, useState } from 'react';
import errorReporter from '@app/errorReporter';

import Swal from 'sweetalert2';
import { getListingSentrilocks, validateSentrilockDevice } from 'api/get';
import styled from 'styled-components';
import { ConfirmationInstructionsJsonType } from '@bb/domain/dist/types/AppointmentConfig';
import { ListingLabelContext } from 'contexts';
import { formatTimeStr } from 'utils';
import axios from 'axios';

import { ListingSentrilocks } from 'api/get/getListingSentrilocks';
import { Icon } from '../Icon';
import { Select, SelectOption } from '../Select';
import { Text } from '../Typography';
import theme from '../theme';

interface SentrilockValidatorProps {
  isOneDayCodeEnabled?: boolean;
  setGetLockboxesFalse: () => void;
  shouldGetLockboxes: boolean;
  listingId: string;
  listingReps: any[];
  sentriLockInfo: Partial<ConfirmationInstructionsJsonType['sentrilockv2']>;
  setSentriLockInfo: (
    info: Partial<ConfirmationInstructionsJsonType['sentrilockv2']>,
  ) => void;
  pendingSentriLockAssignment?: boolean;
  pendingSentriLockAssignmentRetryTime?: string;
  isFirstShowingAgentInstructionsRender: boolean;
  showSentriLockAssignmentWarning: boolean;
  $rootScope?: angular.IRootScopeService;
  ToastService?: any;
}

// this is a helper function for valiateDeviceId
const buildInvalidMessage = (message?: string) => {
  switch (message) {
    case 'NOT_OWNED_LOCKBOX': {
      return 'The lock box is not owned by listing agents. Please sync BrokerBay lock box management with SentriLock system.';
    }
    default: {
      return 'The SentriLock Device ID is invalid';
    }
  }
};

const SentrilockValidator = ({
  isOneDayCodeEnabled,
  setGetLockboxesFalse,
  shouldGetLockboxes,
  listingId,
  sentriLockInfo,
  setSentriLockInfo,
  pendingSentriLockAssignment,
  pendingSentriLockAssignmentRetryTime,
  isFirstShowingAgentInstructionsRender,
  showSentriLockAssignmentWarning,
  $rootScope,
  ToastService,
}: SentrilockValidatorProps) => {
  const [locks, setLocks] = useState<ListingSentrilocks>([]);
  const [selected, setSelected] = useState<(typeof locks)[number] | null>(null);
  const [isSelectedLockboxValid, setIsSelectedLockboxValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState('');
  const { getAvailability } = React.useContext(ListingLabelContext);

  const deselect = () => {
    setSelected(null);
    setIsSelectedLockboxValid(false);
    setSentriLockInfo({
      lbsn: undefined,
      isOneDayCodeAvailable: false,
      isOneDayCodeEnabled: false,
    });
  };

  // Sync the selected lockbox with BrokerBay
  const syncLockbox = async (selectedLock: (typeof locks)[number]) => {
    if (!selectedLock.user) return;

    const newLock = {
      brand: 'sentriLock',
      ...selectedLock, // serial number, user, userMlsNumber, externalId, siteId
    };

    try {
      await axios.post('/single_lockbox', newLock);
      $rootScope?.$evalAsync(() => {
        ToastService?.add({
          type: 'info',
          message: `Lockbox has been synced and assigned to the listing.`,
        });
      });
      return true;
    } catch (err) {
      errorReporter.error(err);
      $rootScope?.$evalAsync(() => {
        ToastService?.add({
          type: 'danger',
          message: `Lockbox failed to sync. Please try again.`,
        });
      });
      return false;
    }
  };

  const validateDeviceId = (selectedLock: (typeof locks)[number]) => {
    if (!selectedLock) return;

    setIsValidating(true);
    validateSentrilockDevice(selectedLock.serialNumber, listingId)
      .then(
        ({
          isValid,
          isOneDayCodeAvailable,
          defaultOneDayCodeEnabled,
          message,
        }) => {
          if (!isValid) {
            setIsSelectedLockboxValid(false);
            setInvalidMessage(buildInvalidMessage(message));
            return;
          }

          setIsSelectedLockboxValid(Boolean(isValid));
          setInvalidMessage('');
          setSentriLockInfo({
            lbsn: selectedLock.serialNumber,
            userMlsNumber: selectedLock.userMlsNumber,
            isOneDayCodeAvailable,
            isOneDayCodeEnabled:
              isOneDayCodeEnabled ?? defaultOneDayCodeEnabled,
          });
        },
      )
      .catch((err: any) => {
        errorReporter.error(err);
        deselect();
      })
      .finally(() => {
        setIsValidating(false);
      });
  };

  const validateAndSyncLockbox = async (
    selectedLock: (typeof locks)[number],
  ) => {
    // Validate the lockbox and sync if necessary
    if (selectedLock.requiresSync) {
      if (await syncLockbox(selectedLock)) {
        // Update requiresSync to false for the selected lock
        const updatedLocks = [...locks];
        const objIndex = updatedLocks.findIndex(
          obj => obj.serialNumber === selectedLock.serialNumber,
        );
        updatedLocks[objIndex].requiresSync = false;
        setLocks(updatedLocks);
      }
    }
    validateDeviceId(selectedLock);
  };

  const select = (selectedLock: (typeof locks)[number]) => {
    if (selectedLock.listing && selectedLock.listing._id !== listingId) {
      Swal.fire({
        title: 'Lockbox Is Already Assigned',
        html: ` <div style="font-weight: 200;"><div>This lockbox is already assigned to another listing: </div>
              <br/>
              <div>${selectedLock.listing.line1} ${
                selectedLock.listing.line2 || ''
              }</div>
              <div>Listing Status: ${getAvailability(
                selectedLock.listing.availability,
              )}</div></div>
              <br/>
              <div>Would you like to remove it and re-assign it to this listing?</div>
              `,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#62cb31',
        confirmButtonText: 'Yes, re-assign',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.dismiss) {
          deselect();
        } else {
          validateAndSyncLockbox(selectedLock);
        }
      });
    } else {
      validateAndSyncLockbox(selectedLock);
    }
  };

  const handleOnOneDayCodeEnabled = () => {
    setSentriLockInfo({
      isOneDayCodeEnabled: !sentriLockInfo?.isOneDayCodeEnabled,
    });
  };

  // Query both current listing Sentrilocks and available agent lockboxes from Sentrilock
  const getAllSentrilocks = async (): Promise<ListingSentrilocks> => {
    // Get listing locks
    const listingLocks = await getListingSentrilocks(listingId);

    listingLocks.sort((a, b) =>
      a.serialNumber > b.serialNumber
        ? 1
        : a.serialNumber < b.serialNumber
          ? -1
          : 0,
    );

    return listingLocks;
  };

  useEffect(() => {
    const fetchListingSentrilocks = async () => {
      try {
        setIsLoading(true);
        const fetchedLocks = await getAllSentrilocks();
        setLocks(fetchedLocks);
        const currentLockbox = fetchedLocks.find(
          ({ serialNumber }) => serialNumber === sentriLockInfo?.lbsn,
        );
        if (currentLockbox) {
          setSelected(currentLockbox);
          if (isFirstShowingAgentInstructionsRender) {
            setIsSelectedLockboxValid(true);
          } else {
            validateAndSyncLockbox(currentLockbox);
          }
        } else {
          setSelected(null);
        }
      } catch (error) {
        errorReporter.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (listingId) {
      setIsLoading(true);
      fetchListingSentrilocks();
    }
  }, [listingId]);

  useEffect(() => {
    const refetchListingSentrilocks = async () => {
      try {
        const fetchedLocks = await getAllSentrilocks();
        setLocks(fetchedLocks);

        const currentLockbox = fetchedLocks.find(
          ({ serialNumber }) => serialNumber === sentriLockInfo?.lbsn,
        );

        if (!currentLockbox) {
          deselect();
        }
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setIsLoading(false);
        setGetLockboxesFalse();
      }
    };
    if (shouldGetLockboxes) {
      refetchListingSentrilocks();
    }
  }, [shouldGetLockboxes]);

  return (
    <div>
      <StyledLockboxValidatorContainer>
        {!selected && locks.length > 0 && (
          <Select
            showSearch
            placeholder="Select"
            onChange={value => {
              const selectedLock = locks.find(
                ({ serialNumber }) => serialNumber === value,
              );
              if (selectedLock) {
                setSelected(selectedLock);
                select(selectedLock);
              }
            }}
            listHeight={230}
          >
            {locks?.map(lock => (
                <SelectOption key={lock.serialNumber} value={lock.serialNumber}>
                  {lock.serialNumber}
                </SelectOption>
              ))}
          </Select>
        )}

        {selected && (
          <div className="form-inline">
            <div className="form-group">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={selected?.serialNumber}
                  disabled
                />
                <span className="input-group-btn">
                  <button onClick={deselect} className="btn btn-danger">
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
        )}
        {!locks?.length && !isLoading && (
          <a
            href="/#/lockboxes"
            className="sentrilock-validator-add-lockbox-link"
          >
            <label style={{ cursor: 'pointer' }} className="error">
              No lock boxes found. Click here to add a lockbox.
            </label>
          </a>
        )}
      </StyledLockboxValidatorContainer>
      {isValidating && <div>Validating...</div>}
      {locks?.length > 0 && selected && !isLoading && !isValidating && (
        <div>
          {isSelectedLockboxValid && (
            <div>
              {!sentriLockInfo?.isOneDayCodeAvailable ? (
                <div>No one day code available.</div>
              ) : (
                <div>
                  <input
                    type="checkbox"
                    checked={sentriLockInfo?.isOneDayCodeEnabled}
                    onChange={handleOnOneDayCodeEnabled}
                  />
                  &nbsp;Enable One Day Code
                </div>
              )}

              <div>
                {pendingSentriLockAssignment ? (
                  <StyledPendingSentriLockAssignmentContainer>
                    <Icon type="antdWarningOutlined" color="blue6" />
                    <StyledPendingSentriLockAssignmentText>
                      SentriLock assignment is pending.
                      <br /> Will auto retry{' '}
                      {pendingSentriLockAssignmentRetryTime
                        ? `at ${formatTimeStr(
                            pendingSentriLockAssignmentRetryTime,
                          )}.`
                        : 'once listing becomes Active.'}
                    </StyledPendingSentriLockAssignmentText>
                  </StyledPendingSentriLockAssignmentContainer>
                ) : (
                  <label style={{ margin: '5px' }} className="text-success">
                    &nbsp;&#10003; The SentriLock Device ID is valid.
                  </label>
                )}
              </div>

              {showSentriLockAssignmentWarning && (
                <div>
                  <label>
                    Ensure you have also assigned this lockbox in the SentriLock
                    portal before saving the showing configuration.
                  </label>
                </div>
              )}
            </div>
          )}
          {!isSelectedLockboxValid && (
            <div>
              <label style={{ margin: '5px' }} className="error">
                {invalidMessage}
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SentrilockValidator;

const StyledLockboxValidatorContainer = styled.div`
  position: relative;
  > input.form-control {
    display: inline-block;
    width: 75%;
    margin: 0;
    border-radius: 4px 0 0 4px;
    &.valid {
      border-color: #62cb31 !important;
    }
    &.invalid {
      border-color: #e74c3c !important;
    }
  }
  > button {
    position: absolute;
    display: inline-block;
    width: 25%;
    height: 100%;
    margin: 0;
    right: 0;
    border-left: 0;
    border-radius: 0 4px 4px 0;
  }
`;

const StyledPendingSentriLockAssignmentContainer = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 5px;
`;

const StyledPendingSentriLockAssignmentText = styled(Text)`
  color: ${theme.blue6};
`;
